import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Recycle, Droplets, GraduationCap, Sparkles } from "lucide-react";

function Yonja() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Recycle,
      title: "Community Activation",
      description: "Mobilizing residents for collective environmental action",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Droplets,
      title: "Sanitation & Hygiene",
      description: "Ensuring proper waste management and clean environments",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: GraduationCap,
      title: "Education & Innovation",
      description: "Teaching sustainable practices and innovative solutions",
      color: "from-purple-500 to-pink-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-emerald-50">
      <Header />
      
      {/* Hero Image Section */}
      <div className="relative pt-24 sm:pt-28">
        <img 
          src="img/kayunga.jpeg" 
          className="w-full h-64 md:h-96 object-cover"
          alt="Yonja Community"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pt-20 sm:pt-24">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              Environmental Initiative
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-green-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent">
                Yonja Community
              </span>
              <br />
              <span className="text-white">Clean Up</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Mission Statement */}
          <div 
            id="mission-content"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['mission-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8">
              Our <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Mission</span>
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-green-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                  Yonja Community Clean Up is a comprehensive <strong className="text-green-700">service project</strong> designed to 
                  activate community-wide sanitation, hygiene education, and environmental innovation. 
                  We ensure proper sanitation and hygiene standards throughout the community, 
                  creating healthier, cleaner environments for all residents.
                </p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div 
            id="features-grid"
            data-animate
            className={`transform transition-all duration-1000 delay-300 ${
              isVisible['features-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{feature.title}</h3>
                    <p className="text-gray-600 text-center leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-16 bg-gradient-to-r from-green-600 via-blue-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Join Our Clean Community Movement
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Partner with us to create cleaner, healthier communities through 
              innovative sanitation and hygiene education programs.
            </p>
            
            <button 
              onClick={() => window.location.href = '/contact-us'}
              className="bg-white text-green-600 px-8 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Get Involved <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Yonja;